"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import LeadButton from "@/component/ui/Lead/LeadButton";
import { subscribeModalSession } from "@/lib/client/modalSession";
import ImagePageCarousel, {
    ImagePageThumbCarousel,
    useImageCarousel,
    type ImagePageCarouselSlide,
} from "@/component/pdp/image-page/ImagePageCarousel";
import ImagePageFilters from "@/component/pdp/image-page/ImagePageFilters";
import { getCarImageName, getCarImageViewLabel } from "@/component/pdp/image-page/galleryData";
import type { GalleryCategory, GalleryCategoryId, GallerySubcategory } from "@/component/pdp/image-page/types";
import type { ColourGalleryVariant, ModelColour } from "./ModelColourShowcaseLib";
import ColourVariantPanel from "./ColourVariantPanel";

type GalleryTab = "all" | "exterior" | "interior" | "colors";

const GALLERY_TABS: { id: GalleryTab; label: string }[] = [
    { id: "all", label: "All" },
    { id: "exterior", label: "Exterior" },
    { id: "interior", label: "Interior" },
    { id: "colors", label: "Colors" },
];

// The shared popup filters (same ones the images modal uses) speak in
// GalleryCategoryId, so translate between the two tab vocabularies.
const TAB_TO_CATEGORY: Record<GalleryTab, GalleryCategoryId> = {
    all: "all",
    exterior: "exterior",
    interior: "interior",
    colors: "colour",
};
const CATEGORY_TO_TAB: Record<GalleryCategoryId, GalleryTab> = {
    all: "all",
    exterior: "exterior",
    interior: "interior",
    colour: "colors",
};

export type ColourGalleryImage = {
    src: string;
    category?: "exterior" | "interior";
    subcategory?: string;
    alt?: string;
    title?: string;
    label?: string;
};

const wrapIndex = (index: number, total: number) => {
    if (total <= 0) return 0;
    return ((index % total) + total) % total;
};

const filterVariantsForColour = (
    variants: ColourGalleryVariant[],
    colour?: ModelColour,
): ColourGalleryVariant[] => {
    const ids = colour?.variantIds;
    if (!ids?.length) return variants;
    const allowed = new Set(ids);
    return variants.filter((variant) => allowed.has(variant.id));
};

// LeadFormModal portals to <body> at z-100, below this popup's z-999, so the
// gallery has to step aside while a lead form is on screen.
const LEAD_MODAL_SESSION_ID = "lead-form-modal";

export type ColourGalleryPopupProps = {
    open: boolean;
    onClose: () => void;
    brandName: string;
    modelName: string;
    colours: ModelColour[];
    images?: string[];
    galleryImages?: ColourGalleryImage[];
    gallerySubcategories?: GallerySubcategory[];
    variants?: ColourGalleryVariant[];
    startIndex?: number;
    initialTab?: GalleryTab;
    modelId?: string;
    variantId?: string | null;
    isUpcoming?: boolean;
    onColourChange?: (index: number) => void;
};

const toColourSlides = (colours: ModelColour[], brandName: string, modelName: string): ImagePageCarouselSlide[] =>
    colours.map((colour) => ({
        id: colour.id,
        src: colour.image || "",
        alt: `${brandName} ${modelName} in ${colour.name}`,
        title: `${brandName} ${modelName} ${colour.name}`,
        label: colour.name,
        swatch: colour.hex,
    }));

type CategorisedSlide = ImagePageCarouselSlide & {
    category?: ColourGalleryImage["category"];
    subcategory?: string;
};

const toImageSlides = (
    images: string[],
    galleryImages: ColourGalleryImage[] | undefined,
    brandName: string,
    modelName: string,
): CategorisedSlide[] => {
    const carName = `${brandName} ${modelName}`.trim();

    if (galleryImages?.length) {
        return galleryImages.map((image, index) => {
            const name = getCarImageName(carName, image);
            return {
                id: `model-image-${index}`,
                src: image.src,
                alt: name,
                title: name,
                label: getCarImageViewLabel(carName, image),
                category: image.category,
                subcategory: image.subcategory,
            };
        });
    }
    return images.map((src, index) => {
        const name = getCarImageName(carName);
        return {
            id: `model-image-${index}`,
            src,
            alt: name,
            title: name,
            label: "",
        };
    });
};

export default function ColourGalleryPopup({
    open,
    onClose,
    brandName,
    modelName,
    colours,
    images = [],
    galleryImages,
    gallerySubcategories = [],
    variants = [],
    startIndex = 0,
    initialTab = "colors",
    modelId,
    variantId,
    isUpcoming = false,
    onColourChange,
}: ColourGalleryPopupProps) {
    const [tab, setTab] = useState<GalleryTab>("colors");
    const [subcategory, setSubcategory] = useState("all");
    const galleryVariants = variants;
    const [selectedColourIndex, setSelectedColourIndex] = useState(startIndex);
    const [mounted, setMounted] = useState(false);
    const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);

    const galleryColours = colours;
    const pendingJumpRef = useRef<number | null>(null);

    const colourSlides = useMemo(
        () => toColourSlides(galleryColours, brandName, modelName),
        [brandName, galleryColours, modelName],
    );

    const imageSlides = useMemo(
        () => toImageSlides(images, galleryImages, brandName, modelName),
        [brandName, galleryImages, images, modelName],
    );
    const hasImageCategories = imageSlides.some((slide) => slide.category);
    const exteriorSlides = useMemo(
        () => imageSlides.filter((slide) => slide.category === "exterior"),
        [imageSlides],
    );
    const interiorSlides = useMemo(
        () => imageSlides.filter((slide) => slide.category === "interior"),
        [imageSlides],
    );

    const allSlides = useMemo(() => [...imageSlides, ...colourSlides], [colourSlides, imageSlides]);

    // Sub-tabs (Front View, Dashboard, ...) narrow the image tabs, exactly like
    // the images modal. The "All" tab only mixes colours in while no sub-tab is picked.
    const activeSubcategory = tab === "colors" ? "all" : subcategory;
    const includesColours = tab === "all" && activeSubcategory === "all";
    const filterBySubcategory = (list: CategorisedSlide[]) =>
        activeSubcategory === "all" ? list : list.filter((slide) => slide.subcategory === activeSubcategory);
    const slides =
        tab === "colors"
            ? colourSlides
            : tab === "all"
              ? includesColours
                  ? allSlides
                  : filterBySubcategory(imageSlides)
              : filterBySubcategory(
                    !hasImageCategories
                        ? imageSlides
                        : tab === "exterior"
                          ? exteriorSlides
                          : interiorSlides,
                );
    const isColorsTab = tab === "colors";
    const modelImageCount = imageSlides.length;
    const carousel = useImageCarousel(slides.length);
    const offersLabel = `View ${new Date().toLocaleString("default", { month: "long" })} Offers`;

    const colourCount = galleryColours.length;
    const visibleGalleryVariants = useMemo(
        () => filterVariantsForColour(galleryVariants, galleryColours[selectedColourIndex]),
        [galleryVariants, galleryColours, selectedColourIndex],
    );

    const tabCounts: Record<GalleryTab, number> = {
        all: allSlides.length,
        // Exterior / Interior only exist when the image API supplied real categories.
        // Without them the images are just "All" — never duplicate them under both.
        exterior: hasImageCategories ? exteriorSlides.length : 0,
        interior: hasImageCategories ? interiorSlides.length : 0,
        colors: colourCount,
    };
    const visibleTabs = GALLERY_TABS.filter((item) => tabCounts[item.id] > 0);
    const filterCategories: GalleryCategory[] = visibleTabs.map((item) => ({
        id: TAB_TO_CATEGORY[item.id],
        label: item.label,
        icon: TAB_TO_CATEGORY[item.id],
    }));
    const filterCounts: Record<GalleryCategoryId, number> = {
        all: tabCounts.all,
        exterior: tabCounts.exterior,
        interior: tabCounts.interior,
        colour: tabCounts.colors,
    };

    useEffect(() => setMounted(true), []);

    useEffect(
        () => subscribeModalSession((activeId) => setIsLeadFormOpen(activeId === LEAD_MODAL_SESSION_ID)),
        [],
    );

    useEffect(() => {
        if (!open) return;
        setTab(initialTab);
        setSubcategory("all");
        const colourIndexForOpen = initialTab === "colors" ? startIndex : selectedColourIndex;
        setSelectedColourIndex(colourIndexForOpen);
        carousel.jumpTo(startIndex);
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
        // Reset only when the popup opens, not when startIndex changes mid-session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialTab]);

    const stepRef = useRef<(delta: -1 | 1) => void>(() => {});

    useEffect(() => {
        if (!open) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
                return;
            }
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                stepRef.current(-1);
                return;
            }
            if (event.key === "ArrowRight") {
                event.preventDefault();
                stepRef.current(1);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose, open]);

    useEffect(() => {
        if (pendingJumpRef.current == null) return;
        const next = pendingJumpRef.current;
        pendingJumpRef.current = null;
        carousel.jumpTo(next);
        // jumpTo is stable; run only after a tab change queued a target index.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const switchToColors = (colorIndex: number) => {
        const next = Math.min(Math.max(colorIndex, 0), Math.max(colourSlides.length - 1, 0));
        pendingJumpRef.current = next;
        setSelectedColourIndex(next);
        onColourChange?.(next);
        setTab("colors");
    };

    const changeSubcategory = (next: string) => {
        setSubcategory(next);
        carousel.jumpTo(0, 1);
    };

    const changeTab = (next: GalleryTab) => {
        setSubcategory("all");
        pendingJumpRef.current = next === "colors" ? selectedColourIndex : 0;
        if (next === tab) {
            carousel.jumpTo(pendingJumpRef.current);
            pendingJumpRef.current = null;
            return;
        }
        setTab(next);
    };

    const selectColour = (index: number) => {
        setSelectedColourIndex(index);
        onColourChange?.(index);
        if (tab === "colors") carousel.jumpTo(index);
        else switchToColors(index);
    };

    const handleGalleryIndex = (index: number, mode: "swipe" | "jump") => {
        if (tab === "all") {
            if (includesColours && colourSlides.length && index >= modelImageCount) {
                switchToColors(index - modelImageCount);
                return;
            }
            if (mode === "jump") carousel.jumpTo(index);
            else carousel.setActiveIndex(index);
            return;
        }

        if (tab === "colors") {
            const nextIndex = wrapIndex(index, colourSlides.length);
            if (mode === "jump") carousel.jumpTo(nextIndex);
            else carousel.setActiveIndex(nextIndex);
            setSelectedColourIndex(nextIndex);
            onColourChange?.(nextIndex);
            return;
        }

        if (mode === "jump") carousel.jumpTo(index);
        else carousel.setActiveIndex(index);
    };

    const goToColour = (index: number) => {
        const last = Math.max(colourSlides.length - 1, 0);
        const next = Math.min(Math.max(index, 0), last);
        if (tab === "colors") {
            carousel.jumpTo(next);
            setSelectedColourIndex(next);
            onColourChange?.(next);
            return;
        }
        switchToColors(next);
    };

    const goToImages = (index: number) => {
        const last = Math.max(modelImageCount - 1, 0);
        pendingJumpRef.current = Math.min(Math.max(index, 0), last);
        setTab("all");
    };

    const handleStep = (delta: number) => {
        const current = carousel.activeIndex;
        const next = current + delta;

        if (includesColours && colourSlides.length) {
            if (next >= modelImageCount) {
                goToColour(next - modelImageCount);
                return true;
            }
            if (next < 0) {
                goToColour(colourSlides.length - 1);
                return true;
            }
        }

        if (tab === "colors" && colourSlides.length > 1) {
            if (next < 0 || next >= colourSlides.length) {
                handleGalleryIndex(wrapIndex(next, colourSlides.length), "jump");
                return true;
            }
        }

        return false;
    };

    stepRef.current = (delta) => {
        if (handleStep(delta)) return;
        handleGalleryIndex(wrapIndex(carousel.activeIndex + delta, slides.length), "jump");
    };

    const closeOnVariantNavigate = (variant: ColourGalleryVariant) => {
        if (variant.href) onClose();
    };

    if (!mounted || !open) return null;

    const heading = `${brandName} ${modelName} Images`;

    const modal = (
        <div
            className={`fixed inset-0 z-[999] flex items-end justify-center bg-black md:items-stretch ${
                isLeadFormOpen ? "hidden" : ""
            }`}
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={heading}
                onClick={(event) => event.stopPropagation()}
                className="relative flex h-[100dvh] w-full flex-col rounded-t-2xl bg-black text-white md:h-[100dvh] md:w-full md:items-center md:justify-center md:rounded-none md:px-8 md:py-6"
            >
                <button
                    type="button"
                    aria-label="Close colour gallery"
                    onClick={onClose}
                    className="absolute right-4 top-3 z-10 hidden h-8 w-8 items-center justify-center text-[22px] leading-none text-white/80 hover:text-white md:flex md:right-5 md:top-4"
                >
                    ×
                </button>

                <div className="flex items-center justify-between px-4 py-3 md:hidden">
                    <h2 className="text-[16px] font-semibold">{heading}</h2>
                    <button
                        type="button"
                        aria-label="Close colour gallery"
                        onClick={onClose}
                        className="flex h-8 w-8 items-center justify-center text-[22px] leading-none text-white/80"
                    >
                        ×
                    </button>
                </div>

                <div className="w-full min-w-0 px-4 md:flex md:w-[682px] md:flex-col md:items-center md:px-0">
                    <ImagePageFilters
                        variant="popup"
                        categories={filterCategories}
                        subcategories={gallerySubcategories}
                        counts={filterCounts}
                        activeCategory={TAB_TO_CATEGORY[tab]}
                        activeSubcategory={activeSubcategory}
                        onCategoryChange={(id) => changeTab(CATEGORY_TO_TAB[id])}
                        onSubcategoryChange={changeSubcategory}
                    />
                </div>

                <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden md:mt-6 md:flex-none md:flex-row md:items-start md:justify-center md:gap-4">
                    {isColorsTab && visibleGalleryVariants.length > 0 && (
                        <ColourVariantPanel
                            variants={visibleGalleryVariants}
                            totalCount={galleryVariants.length}
                            resetKey={selectedColourIndex}
                            onNavigate={onClose}
                        />
                    )}

                    <div className="mt-[100px] min-w-0 flex-1 px-4 pb-2 md:mt-0 md:flex-none md:px-0 md:pb-0">
                        <div className="md:flex md:justify-center">
                            <ImagePageCarousel
                                key={tab}
                                slides={slides}
                                startIndex={carousel.startIndex}
                                activeIndex={carousel.activeIndex}
                                jumpKey={carousel.jumpKey}
                                alignKey={carousel.alignKey}
                                onIndexChange={(index) => handleGalleryIndex(index, "swipe")}
                                onNavigate={(index) => handleGalleryIndex(index, "jump")}
                                onStep={handleStep}
                                countOffset={0}
                                countTotal={slides.length}
                                variant="popup"
                                frameClassName="bg-black"
                                priority
                            />
                        </div>

                        <div className="mt-4 min-h-[42px] md:mx-auto md:mt-5 md:min-h-[56px] md:w-[901px]">
                            {isColorsTab && galleryColours.length > 0 && (
                                <div className="flex items-center justify-start gap-2 overflow-x-auto py-1 scrollbar-hide md:justify-center md:gap-3">
                                    {galleryColours.map((colour, index) => {
                                        const isSelected = index === selectedColourIndex;
                                        return (
                                            <button
                                                key={colour.id}
                                                type="button"
                                                title={colour.name}
                                                aria-label={`View ${colour.name}`}
                                                aria-pressed={isSelected}
                                                onClick={() => selectColour(index)}
                                                className={`h-[51px] w-[51px] shrink-0 cursor-pointer rounded-full border-2 p-[2px] shadow-sm transition-[colors,box-shadow] md:h-[48px] md:w-[48px] ${
                                                    isSelected
                                                        ? "border-[#00c2c1] shadow-[0_2px_8px_rgba(0,140,139,0.28)]"
                                                        : "border-gray-300 hover:border-gray-400"
                                                }`}
                                            >
                                                <span
                                                    className="block h-full w-full rounded-full"
                                                    style={{ background: colour.hex }}
                                                />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {!isColorsTab && (
                                <ImagePageThumbCarousel
                                    slides={slides}
                                    activeId={slides[carousel.activeIndex]?.id}
                                    onThumbClick={(index) => handleGalleryIndex(index, "jump")}
                                    frameClassName="bg-[#1A1A1A]"
                                    itemsPerView={9}
                                    mobileItemsPerView={4.2}
                                />
                            )}
                            {isColorsTab && (
                                <p className="mt-2 text-[12px] text-white/60 md:hidden">
                                    {selectedColourIndex + 1} of {colourCount} colors
                                </p>
                            )}
                        </div>

                        <div className={`mt-3 md:hidden ${isColorsTab ? "min-h-[72px]" : ""}`}>
                            {isColorsTab && visibleGalleryVariants.length > 0 && (
                                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                    {visibleGalleryVariants.map((variant) => {
                                        const className =
                                            "flex w-[128px] min-h-[76px] shrink-0 flex-col justify-between rounded-xl border border-white/60 bg-transparent px-3 py-2.5 text-left text-white";
                                        const body = (
                                            <>
                                                <p title={variant.name} className="line-clamp-2 break-words text-[13px] font-semibold leading-[18px]">
                                                    {variant.name}
                                                </p>
                                                <p className="mt-1.5 text-[12px] text-white/80">{variant.price}</p>
                                            </>
                                        );
                                        if (variant.href) {
                                            return (
                                                <Link
                                                    key={variant.id}
                                                    href={variant.href}
                                                    prefetch={false}
                                                    onClick={() => closeOnVariantNavigate(variant)}
                                                    className={className}
                                                >
                                                    {body}
                                                </Link>
                                            );
                                        }
                                        return (
                                            <div key={variant.id} className={className}>
                                                {body}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3 md:hidden">
                    <LeadButton
                        carId={modelId}
                        variantId={variantId}
                        carName={`${brandName} ${modelName}`}
                        btnText={offersLabel}
                        is_upcoming={isUpcoming}
                        fullWidth
                        className="w-full rounded-lg border border-white bg-transparent py-3 text-[14px] font-semibold text-white"
                    />
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
